import { Layers, Sparkles, Blocks, Database, Building2, GitBranch, Zap, BarChart3, Plug, TrendingUp, Inbox, Bell, Store, Handshake, Crown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRODUCTS, PRODUCT_REVENUE } from "@/data/content";
import { Section, CtaRow } from "@/components/shared";
import { ProductIcon } from "@/lib/icons";

const WHY_ICONS = [Layers, Sparkles, Blocks, Database, Building2, GitBranch, Zap, BarChart3, Plug, TrendingUp];

const CHANNELS = ["WhatsApp", "Messenger", "Instagram", "Website Chat", "Email", "Social Comments"];

const WhyUs = () => {
  const { t } = useLanguage();
  return (
    <div data-testid="why-us-page">
      <Section eyebrow={t("whyUs.eyebrow")} title={t("whyUs.title")} className="pt-20 sm:pt-28 grid-bg">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {t("whyUs.items").map((item, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <div key={item.t} data-testid={`why-item-${i}`} className="card-surface card-surface-hover rounded-2xl p-5">
                <span className="inline-flex w-10 h-10 rounded-xl bg-indigo-500/15 items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-indigo-400" />
                </span>
                <p className="text-xs font-mono2 font-bold uppercase tracking-widest text-white mb-2">{item.t}</p>
                <p className="text-xs text-slate-400 leading-relaxed">{item.d}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section eyebrow={t("whyUs.sharedEyebrow")} title={t("whyUs.sharedTitle")} sub={t("whyUs.sharedSub")} className="bg-[#05070B]">
        <div className="flex flex-wrap justify-center gap-2.5 max-w-5xl mx-auto">
          {t("whyUs.shared").map((s, i) => (
            <span key={s} data-testid={`shared-service-${i}`} className="text-xs font-mono2 text-slate-300 bg-slate-800/70 border border-slate-700/70 rounded-lg px-3.5 py-2">
              {s}
            </span>
          ))}
        </div>
      </Section>

      <Section eyebrow={t("whyUs.crmEyebrow")} title={t("whyUs.crmTitle")} sub={t("whyUs.crmSub")}>
        <div className="max-w-3xl mx-auto glass rounded-3xl p-7">
          <div className="flex flex-wrap gap-2">
            {["Name", "Phone", "Email", "Company", "Source", "Campaign", "Products", "Purchases", "Bookings", "Conversations", "Payments", "Support", "Notes", "Assigned Staff", "Customer Value"].map((f) => (
              <span key={f} className="text-xs text-slate-300 bg-slate-800/80 border border-slate-700 rounded-full px-3.5 py-1.5">{f}</span>
            ))}
          </div>
        </div>
      </Section>

      <Section eyebrow={t("whyUs.commsEyebrow")} title={t("whyUs.commsTitle")} sub={t("whyUs.commsSub")} className="bg-[#05070B]">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="flex flex-wrap md:flex-col gap-2.5 flex-1 justify-center">
            {CHANNELS.map((c) => (
              <span key={c} data-testid={`channel-${c.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs font-medium text-slate-300 bg-slate-800/80 border border-slate-700 rounded-full px-4 py-2 text-center">{c}</span>
            ))}
          </div>
          <span className="text-indigo-400 font-mono2 text-2xl flow-pulse rotate-90 md:rotate-0">→</span>
          <div className="glass rounded-3xl p-8 flex-1 text-center">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 items-center justify-center mb-4">
              <Inbox className="w-7 h-7 text-white" />
            </span>
            <p className="font-heading font-bold text-white text-lg mb-2">ONE INBOX</p>
            <div className="flex justify-center gap-2 text-[10px] font-mono2 uppercase tracking-widest">
              <span className="text-cyan-400">AI</span><span className="text-slate-600">+</span>
              <span className="text-emerald-400">HUMAN</span><span className="text-slate-600">+</span>
              <span className="text-purple-400">CRM</span>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow={t("whyUs.notifEyebrow")} title={t("whyUs.notifTitle")}>
        <div className="max-w-2xl mx-auto space-y-2.5">
          {t("whyUs.notifs").map((n, i) => (
            <div key={n} data-testid={`notif-${i}`} className="card-surface rounded-xl px-5 py-3.5 flex items-center gap-3 text-sm text-slate-300">
              <Bell className="w-4 h-4 text-indigo-400 shrink-0" /> {n}
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={t("whyUs.marketEyebrow")} title={t("whyUs.marketTitle")} sub={t("whyUs.marketSub")} className="bg-[#05070B]">
        <div className="flex flex-wrap justify-center gap-2.5 max-w-4xl mx-auto">
          {["Accounting", "E-Invoice", "Payment Gateway", "WhatsApp", "Bank", "E-Commerce", "Biometric", "E-Signature", "Logistics", "Analytics", "External Apps"].map((m) => (
            <span key={m} className="inline-flex items-center gap-2 text-xs text-slate-300 bg-slate-800/70 border border-dashed border-slate-600 rounded-full px-4 py-2">
              <Store className="w-3.5 h-3.5 text-slate-500" /> {m}
            </span>
          ))}
        </div>
      </Section>

      <Section eyebrow={t("whyUs.partnerEyebrow")} title={t("whyUs.partnerTitle")} sub={t("whyUs.partnerSub")}>
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
          {["Leads", "Customers", "Subscriptions", "Commission", "Renewals", "Support"].map((d) => (
            <div key={d} className="card-surface rounded-2xl p-5 flex items-center gap-3">
              <Handshake className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-sm font-medium text-slate-200">{d}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={t("whyUs.ownerEyebrow")} title={t("whyUs.ownerTitle")} sub={t("whyUs.ownerSub")} className="bg-[#05070B]">
        <div className="max-w-4xl mx-auto glass rounded-3xl p-7 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Crown className="w-5 h-5 text-amber-400" />
            <p className="font-heading font-bold text-white">MRR by Product</p>
          </div>
          <div className="space-y-4">
            {PRODUCT_REVENUE.map((r) => {
              const p = PRODUCTS.find((x) => x.slug === r.slug);
              const pct = parseInt(r.mrr.replace(/\D/g, "")) / 1.2;
              return (
                <div key={r.slug} data-testid={`revenue-${r.slug}`} className="flex items-center gap-4">
                  <span className={`w-24 text-xs font-mono2 ${p?.accent.text || "text-slate-400"}`}>{p?.short}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${p?.accent.grad || "from-indigo-500 to-cyan-400"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-20 text-right text-sm font-bold font-heading text-white">{r.mrr}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-8">
            {[{ k: "Cross-Sell Rate", v: "38%" }, { k: "Avg Revenue / Customer", v: "RM1,695" }, { k: "Customer Lifetime Value", v: "RM61K" }].map((s) => (
              <div key={s.k} className="card-surface rounded-2xl p-4 text-center">
                <p className="text-lg font-extrabold font-heading text-white">{s.v}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{s.k}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16"><CtaRow /></div>
      </Section>
    </div>
  );
};

export default WhyUs;
